import React, {useEffect, useState} from 'react';
import {Link, useLocation, useNavigate, useParams} from 'react-router-dom';
import Layout from '../components/Layout';
import {api} from '../../api';

const IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/gif', 'image/webp', 'image/svg+xml'];
const TEXT_TYPES = ['text/plain', 'text/csv', 'text/html', 'text/css', 'text/javascript', 'application/json', 'application/xml', 'text/xml', 'text/markdown'];
const EDITABLE_TYPES = [
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'application/msword', 'application/vnd.ms-excel', 'application/vnd.ms-powerpoint',
];

function isImage(ct) { return IMAGE_TYPES.some(t => ct?.startsWith(t)); }
function isPdf(ct) { return ct === 'application/pdf'; }
function isText(ct) { return TEXT_TYPES.some(t => ct?.startsWith(t)); }
function isEditable(ct) { return EDITABLE_TYPES.includes(ct); }

const FILE_ICONS = {
  'application/pdf': {color: 'text-red-600', bg: 'bg-red-50', label: 'PDF'},
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': {color: 'text-blue-600', bg: 'bg-blue-50', label: 'DOCX'},
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': {color: 'text-green-600', bg: 'bg-green-50', label: 'XLSX'},
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': {color: 'text-orange-600', bg: 'bg-orange-50', label: 'PPTX'},
  'text/plain': {color: 'text-gray-600', bg: 'bg-gray-50', label: 'TXT'},
  'text/csv': {color: 'text-green-600', bg: 'bg-green-50', label: 'CSV'},
};

function FileIcon({contentType}) {
  const img = contentType?.startsWith('image/');
  const match = FILE_ICONS[contentType];
  const color = match?.color || (img ? 'text-purple-600' : 'text-gray-500');
  const bg = match?.bg || (img ? 'bg-purple-50' : 'bg-gray-50');
  const label = match?.label || (img ? contentType.split('/')[1].toUpperCase() : contentType?.split('/').pop()?.toUpperCase() || '?');
  const icon = img ? (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="m2.25 15.75 5.159-5.159a2.25 2.25 0 0 1 3.182 0l5.159 5.159m-1.5-1.5 1.409-1.409a2.25 2.25 0 0 1 3.182 0l2.909 2.909M3.75 21h16.5A2.25 2.25 0 0 0 22.5 18.75V5.25A2.25 2.25 0 0 0 20.25 3H3.75A2.25 2.25 0 0 0 1.5 5.25v13.5A2.25 2.25 0 0 0 3.75 21Z" />
    </svg>
  ) : (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z" />
    </svg>
  );
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded ${bg} ${color} text-xs font-medium`}>
      {icon}{label}
    </span>
  );
}

export default function DocumentDetail() {
  const {id} = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showDelete, setShowDelete] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [textContent, setTextContent] = useState(null);
  const [showRename, setShowRename] = useState(false);
  const [renameName, setRenameName] = useState('');

  const load = () => {
    api(`/documents/${id}`).then(r => r.ok ? r.json() : null).then(data => { setDoc(data); setLoading(false); });
  };

  useEffect(() => {
    // If returning from editor, delay load to let the save callback complete
    if (location.state?.fromEditor) {
      setLoading(true);
      const t = setTimeout(load, 2000);
      return () => clearTimeout(t);
    }
    load();
  }, [id, location.state]);

  useEffect(() => {
    if (!doc) return;
    const viewable = isImage(doc.contentType) || isPdf(doc.contentType) || isText(doc.contentType);
    if (!viewable) return;
    api(`/documents/${id}/download`).then(r => r.ok ? r.json() : null).then(data => {
      if (!data) return;
      if (isText(doc.contentType)) {
        fetch(data.url).then(r => r.text()).then(setTextContent).catch(() => {});
      } else {
        setPreviewUrl(data.url);
      }
    });
  }, [doc, id]);

  const handleDownload = async () => {
    const res = await api(`/documents/${id}/download`);
    if (res.ok) {
      const data = await res.json();
      const a = document.createElement('a');
      a.href = data.url;
      a.download = doc.fileName;
      a.click();
    }
  };

  const handleDelete = () => {
    const target = doc.folderId ? `/documents?folderId=${doc.folderId}` : '/documents';
    api(`/documents/${id}`, {method: 'DELETE'}).then(() => navigate(target, {state: {successBanner: `"${doc.fileName}" was deleted`}}));
  };

  const handleRename = async (e) => {
    e.preventDefault();
    if (!renameName.trim()) return;
    const res = await api(`/documents/${id}`, {method: 'PATCH', body: JSON.stringify({fileName: renameName.trim()})});
    if (res.ok) { setShowRename(false); load(); }
  };

  const formatSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  if (loading) return <Layout><div className="p-6">Loading...</div></Layout>;
  if (!doc) return <Layout><div className="p-6">Not found.</div></Layout>;

  return (
    <Layout>
      <Link to={doc.folderId ? `/documents?folderId=${doc.folderId}` : '/documents'} className="text-cyan-600 hover:text-cyan-800 mb-4 inline-block">&larr; Back</Link>

      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-900">{doc.fileName}</h2>
          <div className="flex gap-3">
            <button onClick={() => { setShowRename(true); setRenameName(doc.fileName); }} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">Rename</button>
            {isEditable(doc.contentType) && (
              <Link to={`/documents/${id}/edit`} className="px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700">Edit</Link>
            )}
            <button onClick={handleDownload} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">Download</button>
            <button onClick={() => setShowDelete(true)} className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700">Delete</button>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
          <div>
            <label className="block text-sm font-medium text-gray-500">Type</label>
            <div className="mt-1"><FileIcon contentType={doc.contentType} /></div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-500">Size</label>
            <p className="mt-1 text-gray-900">{formatSize(doc.size)}</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-500">Uploaded</label>
            <p className="mt-1 text-gray-900">{new Date(doc.createdAt).toLocaleDateString()}</p>
          </div>
          {doc.description && (
            <div className="sm:col-span-3">
              <label className="block text-sm font-medium text-gray-500">Description</label>
              <p className="mt-1 text-gray-900">{doc.description}</p>
            </div>
          )}
        </div>

        {isImage(doc.contentType) && previewUrl && (
          <div className="border rounded-lg p-4 bg-gray-50 flex justify-center">
            <img src={previewUrl} alt={doc.fileName} className="max-w-full max-h-[70vh] object-contain" />
          </div>
        )}

        {isPdf(doc.contentType) && previewUrl && (
          <div className="border rounded-lg overflow-hidden">
            <iframe src={previewUrl} title={doc.fileName} className="w-full h-[70vh]" />
          </div>
        )}

        {isText(doc.contentType) && textContent !== null && (
          <div className="border rounded-lg bg-gray-50 p-4 overflow-auto max-h-[70vh]">
            <pre className="text-sm text-gray-800 whitespace-pre-wrap">{textContent}</pre>
          </div>
        )}
      </div>

      {showRename && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setShowRename(false)}>
          <form onSubmit={handleRename} onClick={e => e.stopPropagation()} className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Rename Document</h3>
            <input autoFocus type="text" value={renameName} onChange={e => setRenameName(e.target.value)}
              placeholder="File name" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 outline-none" />
            <div className="flex justify-end gap-3 mt-4">
              <button type="button" onClick={() => setShowRename(false)} className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg">Cancel</button>
              <button type="submit" disabled={!renameName.trim()} className="px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 disabled:opacity-50">Save</button>
            </div>
          </form>
        </div>
      )}

      {showDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg p-6 w-96">
            <h3 className="text-lg font-bold text-gray-900 mb-2">Delete Document</h3>
            <p className="text-gray-600 mb-6">Are you sure you want to delete this document? This cannot be undone.</p>
            <div className="flex justify-end gap-3">
              <button onClick={() => setShowDelete(false)} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
              <button onClick={handleDelete} className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700">Delete</button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
